import { useMutation, useQuery } from '@tanstack/react-query';

import type { CMSPlanType, LicenceType, WorkflowsTrialSettings } from '@sine/sine-core-api-types';
import { useNotification } from '@sine/sine-ui-components';

import parseServerError from '../../../helpers/parseServerError';
import { MutationKeys, QueryKeys, queryClient } from '../../../query.client';
import billingService from '../../../services/billing/billing.service';
import type { Card, Currency } from '../../../services/billing/billing.types';
import reportingService from '../../../services/reporting/reporting.service';
import { type AddonType } from '../constants/billing.constants';

export const useBillingMode = (teamId: string | undefined) => {
  const {
    isLoading: isLoadingBillingMode,
    isFetching: isFetchingBillingMode,
    refetch: refetchBillingMode,
    data: billingMode = [],
  } = useQuery({
    enabled: !!teamId,
    queryKey: [QueryKeys.BILLING_MODE, teamId],
    queryFn: async () => billingService.getBillingMode(teamId!),
  });

  return {
    isLoadingBillingMode,
    isFetchingBillingMode,
    refetchBillingMode,
    billingMode,
  };
};

export const useCustomer = (teamId: string | undefined) => {
  const {
    isLoading: isLoadingCustomer,
    isFetching: isFetchingCustomer,
    refetch: refetchCustomer,
    data: customer = null,
  } = useQuery({
    enabled: !!teamId,
    queryKey: [QueryKeys.CUSTOMER, teamId],
    queryFn: async () => billingService.getCustomer(teamId!),
  });

  return {
    isLoadingCustomer,
    isFetchingCustomer,
    refetchCustomer,
    customer,
  };
};

export const useSubscriptions = (teamId: string | undefined) => {
  const {
    isLoading: isLoadingSubscriptions,
    isFetching: isFetchingSubscriptions,
    refetch: refetchSubscriptions,
    data: subscriptions = [],
  } = useQuery({
    enabled: !!teamId,
    queryKey: [QueryKeys.SUBSCRIPTIONS, teamId],
    queryFn: async () => billingService.listSubscriptions(teamId!),
  });

  return {
    isLoadingSubscriptions,
    isFetchingSubscriptions,
    refetchSubscriptions,
    subscriptions,
  };
};

export const useBillingSites = (teamId: string | undefined) => {
  const {
    isLoading: isLoadingBillingSites,
    isFetching: isFetchingBillingSites,
    refetch: refetchBillingSites,
    data: billingSites = [],
  } = useQuery({
    enabled: !!teamId,
    queryKey: [QueryKeys.BILLING_SITES, teamId],
    queryFn: async () => billingService.listSites(teamId!),
  });

  return {
    isLoadingBillingSites,
    isFetchingBillingSites,
    refetchBillingSites,
    billingSites,
  };
};

export const useSiteStatistics = (teamId: string | undefined) => {
  const {
    isLoading: isLoadingSiteStatistics,
    isFetching: isFetchingSiteStatistics,
    refetch: refetchSiteStatistics,
    data: siteStatistics = [],
  } = useQuery({
    enabled: !!teamId,
    queryKey: [QueryKeys.SITE_STATISTICS, teamId],
    queryFn: async () => reportingService.getSiteStatistics({ teamId }),
  });

  return {
    isLoadingSiteStatistics,
    isFetchingSiteStatistics,
    refetchSiteStatistics,
    siteStatistics,
  };
};

export const useBillingContact = (teamId: string | undefined) => {
  const {
    isLoading: isLoadingBillingContact,
    isFetching: isFetchingBillingContact,
    refetch: refetchBillingContact,
    data: billingContact = undefined,
  } = useQuery({
    enabled: !!teamId,
    queryKey: [QueryKeys.BILLING_CONTACT, teamId],
    queryFn: async () => billingService.getBillingContact(teamId!),
  });

  return {
    isLoadingBillingContact,
    isFetchingBillingContact,
    refetchBillingContact,
    billingContact,
  };
};

export const useBillingUsage = (teamId: string | undefined) => {
  const {
    isLoading: isLoadingBillingUsage,
    isFetching: isFetchingBillingUsage,
    data: billingUsage = [],
  } = useQuery({
    enabled: !!teamId,
    queryKey: [QueryKeys.BILLING_USAGE, teamId],
    queryFn: async () => billingService.getBillingUsage(teamId!),
  });

  return {
    isLoadingBillingUsage,
    isFetchingBillingUsage,
    billingUsage,
  };
};

export const useWorkflowsTrialData = (teamId: string | undefined) => {
  const {
    isLoading: isLoadingWorkflowsTrialData,
    isFetching: isFetchingWorkflowsTrialData,
    data: workflowsTrialData = [],
  } = useQuery({
    enabled: !!teamId,
    queryKey: [QueryKeys.WORKFLOWS_TRIAL_DATA, teamId],
    queryFn: async () => billingService.getWorkflowsTrialData(),
  });

  return {
    isLoadingWorkflowsTrialData,
    isFetchingWorkflowsTrialData,
    workflowsTrialData,
  };
};

export const useCancelWorkflowTrial = (teamId: string | undefined, onSuccess: () => void, onCancel: () => void) => {
  const { addNotification } = useNotification();

  return useMutation({
    mutationKey: [MutationKeys.CANCEL_WORKFLOW_TRIAL, teamId],
    mutationFn: () => billingService.cancelWorkflowTrial(),
    onSuccess: async () => {
      await onSuccess();
      onCancel();
      addNotification({
        heading: 'Workflows trial cancelled',
      });
    },
    onError: (error: any) => {
      addNotification({
        variant: 'danger',
        heading: 'Failed to cancel trial',
        message: parseServerError(error),
      });
    },
  });
};

export const useExtendWorkflowTrial = (teamId: string | undefined, onSuccess: () => void, onClose: () => void) => {
  const { addNotification } = useNotification();

  return useMutation({
    mutationKey: [MutationKeys.EXTEND_WORKFLOW_TRIAL, teamId],
    mutationFn: () => billingService.extendWorkflowTrial(),
    onSuccess: async () => {
      await onSuccess();
      onClose();
      addNotification({
        heading: 'Workflows trial extended',
      });
    },
    onError: (error: any) => {
      addNotification({
        variant: 'danger',
        heading: 'Failed to extend trial',
        message: parseServerError(error),
      });
    },
  });
};

export const useSaveWorkflowTrialSettings = (
  teamId: string | undefined,
  onSuccess: () => void,
  onClose: () => void,
) => {
  const { addNotification } = useNotification();

  return useMutation({
    mutationKey: [MutationKeys.SAVE_WORKFLOW_TRIAL_SETTINGS, teamId],
    mutationFn: ({ settings }: { settings: WorkflowsTrialSettings }) =>
      billingService.manageWorkflowTrialSettings(settings),
    onSuccess: async () => {
      await onSuccess();
      onClose();
      addNotification({
        heading: 'Workflows trial settings saved',
      });
    },
    onError: (error: any) => {
      addNotification({
        variant: 'danger',
        heading: 'Failed to save the settings of workflows trial',
        message: parseServerError(error),
      });
    },
  });
};

export const useCreateLicenceAddon = (licenceId: string) => {
  const { addNotification } = useNotification();

  return useMutation({
    mutationKey: [MutationKeys.CREATE_LICENCE_ADDON, licenceId],
    mutationFn: (params: { teamId: string; licenceId: string; addonType: `${AddonType}` }) =>
      billingService.createLicenceAddon(params),
    onSuccess: () => {
      addNotification({
        heading: `Workflows integration enabled`,
      });
    },
    onError: (error: any) => {
      addNotification({
        heading: 'Failed to enable Workflows integration',
        message: parseServerError(error),
        variant: 'danger',
      });
    },
  });
};

export const useCancelLicenceAddon = (licenceId: string) => {
  const { addNotification } = useNotification();

  return useMutation({
    mutationKey: [MutationKeys.CANCEL_LICENCE_ADDON, licenceId],
    mutationFn: (params: { teamId: string; licenceId: string; addon: `${AddonType}` }) =>
      billingService.cancelLicenceAddon(params),
    onSuccess: () => {
      addNotification({
        heading: `Workflows integration disabled`,
      });
    },
    onError: (error: any) => {
      addNotification({
        heading: 'Failed to disable Workflows integration',
        message: parseServerError(error),
        variant: 'danger',
      });
    },
  });
};

export const useCMSPlanData = (teamId: string | undefined) => {
  return useQuery({
    enabled: !!teamId,
    queryKey: [QueryKeys.CMS_PLAN_DATA, teamId],
    queryFn: () => billingService.getCMSPlan(),
  });
};

export const useSaveCMSPlan = (teamId: string | undefined, onSuccess: () => void, onClose: () => void) => {
  const { addNotification } = useNotification();

  return useMutation({
    mutationKey: [MutationKeys.SAVE_CMS_PLAN_DATA, teamId],
    mutationFn: ({ planType, cutOffDate }: any) => billingService.createOrUpdateCMSPlan(planType, cutOffDate),
    onSuccess: async () => {
      // Invalidate the CMS plan data cache
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.CMS_PLAN_DATA, teamId], exact: false });

      await onSuccess();
      onClose();
    },
    onError: (error: any) => {
      addNotification({
        variant: 'danger',
        heading: 'Failed to save the data of workflows plan',
        message: parseServerError(error),
      });
    },
  });
};

export const usePrices = (teamId: string, currency: Currency, all?: boolean) => {
  const {
    isLoading: isLoadingPrices,
    isFetching: isFetchingPrices,
    refetch: refetchPrices,
    data: prices = [],
  } = useQuery({
    queryKey: [QueryKeys.PRICES, teamId, currency, all],
    queryFn: () => billingService.listPrices(teamId, currency, all),
    enabled: !!teamId && !!currency,
  });

  return {
    isLoadingPrices,
    isFetchingPrices,
    refetchPrices,
    prices,
  };
};

export const useUpdateSubscription = (subscriptionId: string, onSuccess: () => void) => {
  const { addNotification } = useNotification();

  const { mutateAsync: updateSubscription, isLoading: isUpdatingSubscription } = useMutation({
    mutationKey: [MutationKeys.UPDATE_SUBSCRIPTION, subscriptionId],
    mutationFn: (licenseUpdates: Array<{ id: string; newPriceId: string; siteId?: string }>) =>
      billingService.updateSubscription(subscriptionId, licenseUpdates),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: any) => {
      addNotification({
        heading: 'An error occurred',
        message: parseServerError(error),
        variant: 'danger',
      });
    },
  });

  return {
    updateSubscription,
    isUpdatingSubscription,
  };
};

export const useCreateSubscription = (teamId: string) => {
  const { addNotification } = useNotification();

  const { mutateAsync: createSubscription, isLoading: isCreatingSubscription } = useMutation({
    mutationKey: [MutationKeys.CREATE_SUBSCRIPTION, teamId],
    mutationFn: (licences: Array<{ plan: string; site?: string }>) =>
      billingService.createSubscription({ team: teamId, licences }),
    onSuccess: () => {
      void queryClient.invalidateQueries([QueryKeys.TRIAL_DATA]);
    },
    onError: (error: any) => {
      addNotification({
        heading: 'An error occurred',
        message: parseServerError(error),
        variant: 'danger',
      });
    },
  });

  return {
    createSubscription,
    isCreatingSubscription,
  };
};

export const useUpdateCards = (teamId: string, onSuccess: () => void) => {
  const { addNotification } = useNotification();

  const { mutateAsync: updateCards, isLoading: isUpdatingCards } = useMutation({
    mutationKey: [MutationKeys.UPDATE_CARDS, teamId],
    mutationFn: (cards: Array<Pick<Card, 'id' | 'default'>>) => billingService.updateCards(teamId, cards),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: any) => {
      addNotification({
        heading: 'An error occurred',
        message: parseServerError(error),
        variant: 'danger',
      });
    },
  });

  return {
    updateCards,
    isUpdatingCards,
  };
};

export const useUpdateManualSubscription = (teamId: string, onSuccess?: () => void) => {
  const { addNotification } = useNotification();

  const { mutateAsync: updateManualSubscription, isLoading: isUpdatingManualSubscription } = useMutation({
    mutationKey: [MutationKeys.UPDATE_MANUAL_SUBSCRIPTION, teamId],
    mutationFn: (params: {
      subscriptionId: string;
      startDate: string;
      endDate: string;
      items: Array<{ id: string | null; type: string; note: string }>;
    }) =>
      billingService.updateManualSubscription(params.subscriptionId, params.startDate, params.endDate, params.items),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error: any) => {
      addNotification({
        heading: 'Failed to update subscription',
        message: parseServerError(error),
        variant: 'danger',
      });
    },
  });

  return {
    updateManualSubscription,
    isUpdatingManualSubscription,
  };
};

export const useCreateManualLicence = (teamId: string, onSuccess?: () => void) => {
  const { addNotification } = useNotification();

  const { mutateAsync: createManualLicence, isLoading: isCreatingManualLicence } = useMutation({
    mutationKey: [MutationKeys.CREATE_MANUAL_LICENCE, teamId],
    mutationFn: (params: {
      subscriptionId: string;
      licences: Array<{ siteId?: string; plan: `${LicenceType}`; note: string }>;
    }) =>
      billingService.createManualLicence({
        teamId,
        subscriptionId: params.subscriptionId,
        licences: params.licences,
      }),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error: any) => {
      addNotification({
        heading: 'Failed to create licence',
        message: parseServerError(error),
        variant: 'danger',
      });
    },
  });

  return {
    createManualLicence,
    isCreatingManualLicence,
  };
};

export const useCreateManualSubscription = (teamId: string, onSuccess?: () => void) => {
  const { addNotification } = useNotification();

  const { mutateAsync: createManualSubscription, isLoading: isCreatingManualSubscription } = useMutation({
    mutationKey: [MutationKeys.CREATE_MANUAL_SUBSCRIPTION, teamId],
    mutationFn: (params: {
      convertToStripe: boolean;
      startDate: Date | string;
      endDate: Date | string;
      licences: Array<{ siteId?: string; plan: `${LicenceType}`; note: string }>;
      cmsPlan?: `${CMSPlanType}`;
    }) =>
      billingService.createManualSubscription({
        teamId,
        convertToStripe: params.convertToStripe,
        startDate: params.startDate,
        endDate: params.endDate,
        licences: params.licences,
        cmsPlan: params.cmsPlan,
      }),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error: any) => {
      addNotification({
        heading: 'Failed to create subscription',
        message: parseServerError(error),
        variant: 'danger',
      });
    },
  });

  return {
    createManualSubscription,
    isCreatingManualSubscription,
  };
};
